<template>
    <!-- Outside the section on purpose: aria-busy on the section tells VoiceOver to hold live
         updates inside it until the load ends, by which time this text is already gone. -->
    <span class="sr-only" role="status" aria-live="polite" aria-atomic="true">{{ loadingAnnouncement }}</span>
    <section class="flex flex-col gap-6" :aria-label="t('connections.pairPerspective.sectionLabel')" :aria-busy="isFetching">
        <p v-if="isFetching && !retrying" class="body-small text-ink-muted">{{ t("connections.pairPerspective.loading") }}</p>
        <!-- The pressed button stays mounted while the retry runs: removing a focused control drops
             VoiceOver to the page and the loading announcement with it. -->
        <div v-else-if="error || retrying" class="flex flex-col items-start gap-3">
            <p v-if="retrying" class="body-small text-ink-muted">{{ t("connections.pairPerspective.loading") }}</p>
            <p v-else role="alert" class="body-small">{{ t("connections.pairPerspective.loadError") }}</p>
            <BaseButton theme="neutral" size="sm" :disabled="retrying" @click="retryComparison">{{ t("common.tryAgain") }}</BaseButton>
        </div>
        <template v-else-if="data?.comparison">
            <PairPerspectiveCards
                :comparison="data.comparison"
                :advice="advice"
                :is-generating="isGenerating"
                :advice-error="adviceError ? t('connections.pairPerspective.adviceError') : null"
                :is-starting-chat="isStartingChat"
                :chat-error="chatError"
                @retry="generateAdvice"
                @discuss="emit('discuss', $event)" />
            <PairPerspectiveDimensions :comparison="data.comparison" :is-starting-chat="isStartingChat" :chat-error="chatError" @discuss="emit('discuss', $event)" />
        </template>
        <BaseCard v-else-if="data?.unavailable_reason" padding="lg">
            <h3 class="title-section mb-2">{{ t("connections.pairPerspective.dynamicTitle") }}</h3>
            <p class="body-small text-ink-muted">{{ data.unavailable_reason }}</p>
        </BaseCard>
    </section>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import type { PairAdvicePending, PairAdviceResponse, PairPerspectiveResponse } from "~vue/api/generated/pair-perspective";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import { useLoadingAnnouncement } from "~vue/composables/useLoadingAnnouncement";
import PairPerspectiveCards from "~vue/Connections/PairPerspectiveCards.vue";
import PairPerspectiveDimensions from "~vue/Connections/PairPerspectiveDimensions.vue";
import { onBeforeUnmount, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps<{ url: string; isStartingChat: boolean; chatError: string | null }>();
const emit = defineEmits<{ discuss: [message: string] }>();
const { t } = useI18n();

const { data, error, isFetching, execute, abort } = useFetch(props.url, { immediate: false }).get().json<PairPerspectiveResponse>();
const retrying = ref(false);
const loadingAnnouncement = useLoadingAnnouncement(isFetching, () => (retrying.value ? t("connections.pairPerspective.retrying") : t("connections.pairPerspective.loading")));
const advice = ref<PairAdviceResponse | null>(null);
const isGenerating = ref(false);
const adviceError = ref(false);
const requestBody = ref({ retry: false });
const {
    data: result,
    error: generationError,
    execute: generate,
    abort: abortAdvice,
    statusCode: adviceStatus,
} = useFetch(props.url, { immediate: false }).post(requestBody).json<PairAdviceResponse | PairAdvicePending>();

let disposed = false;
let pollTimer: ReturnType<typeof globalThis.setTimeout> | undefined;

async function pollAdvice() {
    await generate();
    if (disposed) {
        return;
    }
    requestBody.value = { retry: false };
    if (adviceStatus.value === 404 || adviceStatus.value === 409) {
        data.value = null;
        advice.value = null;
        isGenerating.value = false;
        await execute();
        return;
    }
    if (generationError.value || !result.value) {
        adviceError.value = true;
        isGenerating.value = false;
        return;
    }
    if (!("interpretation" in result.value)) {
        pollTimer = globalThis.setTimeout(pollAdvice, 1500);
        return;
    }
    advice.value = result.value;
    isGenerating.value = false;
}

async function generateAdvice() {
    if (disposed || isGenerating.value) {
        return;
    }
    adviceError.value = false;
    isGenerating.value = true;
    requestBody.value = { retry: true };
    await pollAdvice();
}

async function loadComparison() {
    await execute();
    if (!disposed && data.value?.comparison) {
        await generateAdvice();
    }
}

async function retryComparison() {
    retrying.value = true;
    try {
        await execute();
    } finally {
        retrying.value = false;
    }
    if (!disposed && data.value?.comparison) {
        await generateAdvice();
    }
}

onMounted(loadComparison);
onBeforeUnmount(() => {
    disposed = true;
    abort();
    abortAdvice();
    globalThis.clearTimeout(pollTimer);
});
</script>
